import React, { useContext } from 'react';
import { Link } from 'react-router-dom';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { Hyperlink, LoadingIndicator } from '@motorway/mw-highway-code/alpha';

import { FSL_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsFinanceSettlementLetter';
import { safeLocalStorage } from 'Utilities/storages';

import { FinanceSettlementProviderContext } from '../../DocumentCaptureContexts';
import DocumentCaptureButtons from '../../shared/DocumentCaptureButtons/DocumentCaptureButtons';
import { ExampleDocuments } from '../ExampleDocuments/ExampleDocuments';
import { EXAMPLE_DOCUMENTS } from '../ExampleDocuments/ExampleDocuments.helpers';
import { useImageManager } from '../hooks/useImageManager';
import LocalTexts from '../UploadDocumentsDRFT.texts.json';

import { ConnectFinanceProvider } from './ConnectFinanceProvider';

import styles from '../UploadDocumentsSummarySection.scss';

interface FinanceProviderSectionProps {
  enquiryId: string;
  provider: string;
  sellingVrm?: string;
  vrm: string;
}

export const FinanceProviderSection = ({ enquiryId, provider, sellingVrm, vrm }: FinanceProviderSectionProps) => {
  const kind = 'finance_settlement_letter';

  const LocalT = new MDText(LocalTexts);
  const {
    hasUploadedDocuments,
  } = useImageManager();
  const { financeAgreement, setFinanceAgreement } = useContext(FinanceSettlementProviderContext);

  const titleExampleDocuments = LocalT.translate(`pages.${kind}.titleExampleDocuments`) as string;
  const modalDescription = LocalT.translate(`pages.${kind}.modalText`, { notFound: null }) as string;

  const actionLink = hasUploadedDocuments ? LocalT.translate('unableToAcceptText.seeExample') : LocalT.translate('exampleDocuments.learnMore');

  const onLinkClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    // TO-DO: This is a client side only state, so we need to use localStorage as well to persist
    setFinanceAgreement?.({ ...financeAgreement, optedOut: true });
    safeLocalStorage.setItem('useFinanceSettlementApi', false);
    FSL_SNOW_PLOW_EVENTS.ALREADY_HAVE_FSL_LETTER_CLICK();
  };

  return <section className={styles.uploadDocumentsSummarySection} data-testid={'financeProviderSection'}>
    <h2 className={styles.summaryTitleDRFT}>Finance Settlement Letter</h2>
    <div className={styles.summaryTextContainer}>
      <p className={styles.summaryText} data-testid='subtitleFinanceLetter'>
        {LocalT.translate(`pages.${kind}.weNeedThisLetter`)}
      </p>
      <ExampleDocuments
        {...EXAMPLE_DOCUMENTS[kind as keyof typeof EXAMPLE_DOCUMENTS] as Record<string, unknown>}
        actionLink={actionLink as string}
        documentTitle={titleExampleDocuments}
        modalDescription={modalDescription}
      />
    </div>

    <div className={styles.connectProvider}>
      {!provider
        ? <LoadingIndicator />
        : <ConnectFinanceProvider enquiryId={enquiryId} provider={provider} sellingVrm={sellingVrm} vrm={vrm} />
      }
    </div>

    <div className={styles.bodyLink}>
      <Hyperlink data-testid='linkToExistingLetter' label={LocalT.translate(`pages.${kind}.alreadyHaveFSL`)} onClick={onLinkClick} />
    </div>

    <DocumentCaptureButtons>
      <Button
        fullWidth
        as={Link}
        data-testid='backToSummary'
        label={'Back to summary'}
        onClick={() => FSL_SNOW_PLOW_EVENTS.BACK_TO_SUMMARY_BUTTON_CLICK({ url: 'upload-documents' })}
        to={'upload-documents'}
        variant='secondary'
      />
    </DocumentCaptureButtons>
  </section>;
};
